import {
  FaceLandmarker,
  FilesetResolver,
  HandLandmarker,
} from "@mediapipe/tasks-vision";
import {
  createFlipState,
  updateFingertipFlipState,
} from "./src/scoring-core.js";
import {
  completeRemoteRun,
  fetchRemoteLeaderboard,
  fetchRemoteProfile,
  finishRemoteRun,
  remoteLeaderboardEnabled,
  startRemoteRun,
  supabaseAuthEnabled,
} from "./src/leaderboard-api.js";
import { supabase } from "./src/supabase-client.js";
import { validateUsername } from "./src/username-validation.js";
import { formatLeaderboardDate } from "./src/leaderboard-format.js";
import { getLeaderboardTrophy } from "./src/leaderboard-trophies.js";

const APP_VERSION = "0.1.0";
const ROUND_LENGTH_MS = 20_000;
const COUNTDOWN_MS = 3_000;
const FACE_INTERVAL_IDLE_MS = 110;
const FACE_INTERVAL_PLAYING_MS = 220;
const MOBILE_FACE_INTERVAL_IDLE_MS = 150;
const MOBILE_FACE_INTERVAL_PLAYING_MS = 300;
const SCORE_COOLDOWN_MS = 34;
const COUNTDOWN_AUDIO_START_VOLUME = 0.5;
const CHALLENGE_AUDIO_FULL_VOLUME = 1;
const AUTH_PROFILE_CACHE_KEY = "speed-scuba-auth-profile";
const AUTH_PROFILE_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const LEADERBOARD_KEY = "scuba-count-local-leaderboard";
const WASM_BASE = "/vendor/mediapipe/wasm";
const HAND_MODEL = "/vendor/mediapipe/models/hand_landmarker.task";
const FACE_MODEL = "/vendor/mediapipe/models/face_landmarker.task";

const dom = {
  accountMessage: document.querySelector("#accountMessage"),
  accountName: document.querySelector("#accountName"),
  authCloseButton: document.querySelector("#authCloseButton"),
  authEmail: document.querySelector("#authEmail"),
  authEmailLabel: document.querySelector("#authEmailLabel"),
  authForm: document.querySelector("#authForm"),
  authMessage: document.querySelector("#authMessage"),
  authModal: document.querySelector("#authModal"),
  authPassword: document.querySelector("#authPassword"),
  authPasswordLabel: document.querySelector("#authPasswordLabel"),
  authSubmitButton: document.querySelector("#authSubmitButton"),
  authTitle: document.querySelector("#authTitle"),
  authUsername: document.querySelector("#authUsername"),
  authUsernameLabel: document.querySelector("#authUsernameLabel"),
  cameraEmpty: document.querySelector("#cameraEmpty"),
  cameraFrame: document.querySelector("#cameraFrame"),
  cameraMessage: document.querySelector("#cameraMessage"),
  challengeAudio: document.querySelector("#challengeAudio"),
  countdown: document.querySelector("#countdown"),
  feedback: document.querySelector("#feedback"),
  gameScreen: document.querySelector("#gameScreen"),
  homeAccountStatus: document.querySelector("#homeAccountStatus"),
  homeAuthButton: document.querySelector("#homeAuthButton"),
  homeLeaderboard: document.querySelector("#homeLeaderboard"),
  homeLeaderboardButton: document.querySelector("#homeLeaderboardButton"),
  homeLeaderboardStatus: document.querySelector("#homeLeaderboardStatus"),
  homeMessage: document.querySelector("#homeMessage"),
  homeScreen: document.querySelector("#homeScreen"),
  homeSignOutButton: document.querySelector("#homeSignOutButton"),
  leaderboard: document.querySelector("#leaderboard"),
  leaderboardCloseButton: document.querySelector("#leaderboardCloseButton"),
  leaderboardModal: document.querySelector("#leaderboardModal"),
  leaderboardStatus: document.querySelector("#leaderboardStatus"),
  loginTab: document.querySelector("#loginTab"),
  openAuthButton: document.querySelector("#openAuthButton"),
  overlay: document.querySelector("#overlay"),
  playButton: document.querySelector("#playButton"),
  saveButton: document.querySelector("#saveButton"),
  saveStatus: document.querySelector("#saveStatus"),
  score: document.querySelector("#score"),
  scoreForm: document.querySelector("#scoreForm"),
  scorePulse: document.querySelector("#scorePulse"),
  signOutButton: document.querySelector("#signOutButton"),
  signupTab: document.querySelector("#signupTab"),
  startButton: document.querySelector("#startButton"),
  timer: document.querySelector("#timer"),
  video: document.querySelector("#video"),
};

const state = {
  audioSessionId: 0,
  audioUnlocked: false,
  authMode: "signup",
  authSession: null,
  authReady: false,
  autoStartArmed: true,
  cameraReady: false,
  detectorsReady: false,
  faceLandmarker: null,
  handLandmarker: null,
  phase: "idle",
  countdownEndAt: 0,
  lastFaceAnchor: null,
  lastFaceInferenceAt: -Infinity,
  lastSavedScore: null,
  lastVideoTime: -1,
  flip: createFlipState(),
  loopStarted: false,
  mobileExperience: isMobileExperience(),
  pendingSaveRequested: false,
  profile: null,
  remoteRunStartPromise: null,
  roundEndAt: 0,
  score: 0,
  savingScore: false,
  telemetry: createTelemetryState(),
};

hydrateCachedProfile();
renderLeaderboard();
updateTimer(ROUND_LENGTH_MS);
renderAuthState();
void initAuth();

dom.playButton.addEventListener("click", startCameraAndModels);
dom.startButton.addEventListener("click", () => resetRound({ requirePoseRelease: true }));
dom.scoreForm.addEventListener("submit", saveScore);
dom.openAuthButton.addEventListener("click", openAccountModal);
dom.homeAuthButton.addEventListener("click", openAccountModal);
dom.homeLeaderboardButton.addEventListener("click", openLeaderboardModal);
dom.signOutButton.addEventListener("click", signOut);
dom.homeSignOutButton.addEventListener("click", signOut);
dom.authCloseButton.addEventListener("click", closeAuthModal);
dom.leaderboardCloseButton.addEventListener("click", closeLeaderboardModal);
dom.signupTab.addEventListener("click", () => setAuthMode("signup"));
dom.loginTab.addEventListener("click", () => setAuthMode("login"));
dom.authForm.addEventListener("submit", handleAuthSubmit);
dom.leaderboardModal.addEventListener("click", (event) => {
  if (event.target === dom.leaderboardModal) {
    closeLeaderboardModal();
  }
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !dom.leaderboardModal.classList.contains("hidden")) {
    closeLeaderboardModal();
  }
});

async function initAuth() {
  if (!supabaseAuthEnabled || !supabase) {
    state.authReady = true;
    renderAuthState();
    return;
  }

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      throw error;
    }

    state.authSession = data.session ?? null;
    if (state.authSession) {
      const profile = await loadProfile();
      if (!profile) {
        await clearIncompleteAccountSession();
      }
    } else {
      state.profile = null;
      clearCachedProfile();
    }
  } catch (error) {
    console.warn("Could not restore auth session.", error);
  } finally {
    state.authReady = true;
    renderAuthState();
  }

  supabase.auth.onAuthStateChange((_event, session) => {
    state.authSession = session ?? null;
    if (!session) {
      state.profile = null;
      clearCachedProfile();
      renderAuthState();
      return;
    }

    void loadProfile().then(() => {
      renderAuthState();
      void savePendingScoreIfReady();
    });
  });
}

async function loadProfile({ throwOnError = false } = {}) {
  const token = getAccessToken();
  const userId = state.authSession?.user?.id ?? null;
  if (!token) {
    state.profile = null;
    clearCachedProfile();
    return null;
  }

  try {
    const response = await fetchRemoteProfile(token);
    const profile = response?.profile ?? null;
    state.profile = profile;
    if (state.profile) {
      cacheProfile(state.profile);
    } else {
      clearCachedProfile();
    }
    return state.profile;
  } catch (error) {
    console.warn("Could not load profile.", error);
    const cachedProfile = readCachedProfile();
    if (!throwOnError && cachedProfile && (!userId || cachedProfile.userId === userId)) {
      state.profile = cachedProfile;
      return state.profile;
    }

    state.profile = null;
    if (throwOnError) {
      throw error;
    }
    return null;
  }
}

function openAuthModal(mode = "signup") {
  if (!supabaseAuthEnabled || !supabase) {
    setAuthMessage("Account login is not configured for this build.");
    return;
  }

  setAuthMode(mode);
  setAuthMessage("");
  dom.authModal.classList.remove("hidden");
  const focusTarget = mode === "login" ? dom.authEmail : dom.authUsername;
  focusTarget.focus();
}

function openAccountModal() {
  openAuthModal("signup");
}

function closeAuthModal() {
  dom.authModal.classList.add("hidden");
  setAuthMessage("");
}

async function openLeaderboardModal() {
  dom.leaderboardModal.classList.remove("hidden");
  dom.homeLeaderboard.innerHTML = "";
  setLeaderboardStatus("Loading leaderboard...", dom.homeLeaderboardStatus);
  dom.leaderboardCloseButton.focus();

  try {
    await renderLeaderboard({
      list: dom.homeLeaderboard,
      status: dom.homeLeaderboardStatus,
    });
  } catch (error) {
    console.warn("Could not load home leaderboard.", error);
    setLeaderboardStatus("Leaderboard unavailable.", dom.homeLeaderboardStatus);
  }
}

function closeLeaderboardModal() {
  dom.leaderboardModal.classList.add("hidden");
}

function setAuthMode(mode) {
  if (mode !== "login") {
    mode = "signup";
  }

  state.authMode = mode;
  const signup = mode === "signup";
  const login = mode === "login";

  dom.authTitle.textContent = "Join leaderboard";
  dom.signupTab.classList.toggle("active", signup);
  dom.loginTab.classList.toggle("active", login);
  dom.signupTab.textContent = "Sign up";
  dom.loginTab.classList.remove("hidden");
  dom.authUsernameLabel.classList.toggle("hidden", login);
  dom.authUsername.classList.toggle("hidden", login);
  dom.authEmailLabel.classList.remove("hidden");
  dom.authEmail.classList.remove("hidden");
  dom.authPasswordLabel.classList.remove("hidden");
  dom.authPassword.classList.remove("hidden");
  dom.authUsername.required = signup;
  dom.authEmail.required = true;
  dom.authPassword.required = true;
  dom.authPassword.autocomplete = signup ? "new-password" : "current-password";
  dom.authSubmitButton.textContent = signup ? "Create account" : "Log in";
}

async function handleAuthSubmit(event) {
  event.preventDefault();
  if (!supabase) {
    return;
  }

  setAuthMessage("");
  setAuthLoading(true);

  try {
    if (state.authMode === "signup") {
      await signUp();
    } else {
      await logIn();
    }
  } catch (error) {
    console.error(error);
    setAuthMessage(error.message || "Account action failed.");
  } finally {
    setAuthLoading(false);
    renderAuthState();
  }
}

async function signUp() {
  const username = readValidatedUsername();
  const email = dom.authEmail.value.trim();
  const password = dom.authPassword.value;

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        username,
      },
    },
  });

  if (error) {
    throw error;
  }

  if (!data.session) {
    setAuthMessage("Check your email, then log in to save your score.");
    setAuthMode("login");
    return;
  }

  state.authSession = data.session;
  let profile;
  try {
    profile = await loadProfile({ throwOnError: true });
  } catch {
    await clearIncompleteAccountSession();
    throw new Error("Could not verify your leaderboard profile. Please try again.");
  }

  if (!profile) {
    await clearIncompleteAccountSession();
    throw new Error("Account signup did not create a leaderboard profile. Please try again.");
  }

  closeAuthModal();
  await savePendingScoreIfReady();
}

async function logIn() {
  const email = dom.authEmail.value.trim();
  const password = dom.authPassword.value;
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  state.authSession = data.session ?? null;
  await loadProfile();

  if (!state.profile) {
    await clearIncompleteAccountSession();
    throw new Error("This account is missing a leaderboard username. Please sign up with a username to save scores.");
  }

  closeAuthModal();
  await savePendingScoreIfReady();
}

function readValidatedUsername() {
  const validation = validateUsername(dom.authUsername.value);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  return validation.username;
}

async function signOut() {
  if (!supabase) {
    return;
  }

  await supabase.auth.signOut();
  state.authSession = null;
  state.profile = null;
  state.pendingSaveRequested = false;
  clearCachedProfile();
  closeAuthModal();
  renderAuthState();
}

async function clearIncompleteAccountSession() {
  if (supabase) {
    await supabase.auth.signOut().catch((error) => {
      console.warn("Could not clear incomplete account session.", error);
    });
  }

  state.authSession = null;
  state.profile = null;
  clearCachedProfile();
}

function getAccessToken() {
  return state.authSession?.access_token ?? null;
}

function renderAuthState() {
  const username = state.profile?.username ?? null;
  const signedIn = Boolean(state.authSession) || (!state.authReady && Boolean(username));
  const checkingAccount = !state.authReady && !username;

  dom.accountName.textContent = username ? `@${username}` : "Guest";
  dom.accountMessage.textContent = username
    ? "Scores save to the global leaderboard."
    : checkingAccount
      ? "Checking your account..."
      : "Sign up or log in to save to the leaderboard.";
  dom.homeAccountStatus.textContent = username
    ? `Signed in as @${username}`
    : checkingAccount
      ? "Checking account"
      : "Playing as guest";

  dom.openAuthButton.classList.toggle("hidden", checkingAccount || (signedIn && Boolean(username)));
  dom.homeAuthButton.classList.toggle("hidden", checkingAccount || (signedIn && Boolean(username)));
  dom.signOutButton.classList.toggle("hidden", !signedIn);
  dom.homeSignOutButton.classList.toggle("hidden", !signedIn);
  dom.openAuthButton.textContent = "Sign up / Log in";
  dom.homeAuthButton.textContent = "Sign up / Log in";

  renderSaveState();
}

function hydrateCachedProfile() {
  const cachedProfile = readCachedProfile() ?? readStoredSupabaseProfile();
  if (cachedProfile) {
    state.profile = cachedProfile;
  }
}

function readCachedProfile() {
  try {
    const raw = localStorage.getItem(AUTH_PROFILE_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const cached = JSON.parse(raw);
    const cachedAt = Number(cached?.cachedAt);
    if (!Number.isFinite(cachedAt) || Date.now() - cachedAt > AUTH_PROFILE_CACHE_MAX_AGE_MS) {
      clearCachedProfile();
      return null;
    }

    const validation = validateUsername(cached?.username);
    if (!validation.ok) {
      clearCachedProfile();
      return null;
    }

    return {
      userId: typeof cached.userId === "string" ? cached.userId : null,
      username: validation.username,
      usernameNormalized: validation.usernameNormalized,
    };
  } catch {
    clearCachedProfile();
    return null;
  }
}

function readStoredSupabaseProfile() {
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith("sb-") || !key.endsWith("-auth-token")) {
        continue;
      }

      const stored = JSON.parse(localStorage.getItem(key) ?? "{}");
      const user = stored?.user ?? stored?.currentSession?.user ?? stored?.session?.user;
      const username = user?.user_metadata?.username;
      const validation = validateUsername(username);
      if (!validation.ok) {
        continue;
      }

      return {
        userId: typeof user.id === "string" ? user.id : null,
        username: validation.username,
        usernameNormalized: validation.usernameNormalized,
      };
    }
  } catch {
    return null;
  }

  return null;
}

function cacheProfile(profile) {
  const validation = validateUsername(profile?.username);
  if (!validation.ok) {
    clearCachedProfile();
    return;
  }

  localStorage.setItem(
    AUTH_PROFILE_CACHE_KEY,
    JSON.stringify({
      cachedAt: Date.now(),
      userId: typeof profile.userId === "string" ? profile.userId : null,
      username: validation.username,
      usernameNormalized: validation.usernameNormalized,
    }),
  );
}

function clearCachedProfile() {
  localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
}

function renderSaveState(message) {
  if (message) {
    dom.saveStatus.textContent = message;
  } else if (state.lastSavedScore == null) {
    dom.saveStatus.textContent = "Finish a run to save your score.";
  } else if (remoteLeaderboardEnabled && !state.authSession) {
    dom.saveStatus.textContent = "Sign up or log in to put this score on the leaderboard.";
  } else if (remoteLeaderboardEnabled && !state.profile) {
    dom.saveStatus.textContent = "Sign up or log in to put this score on the leaderboard.";
  } else if (state.profile?.username) {
    dom.saveStatus.textContent = `Ready to save as @${state.profile.username}.`;
  } else {
    dom.saveStatus.textContent = "Ready to save locally.";
  }

  dom.saveButton.textContent =
    remoteLeaderboardEnabled && !state.authSession
      ? "Sign up to save"
      : remoteLeaderboardEnabled && !state.profile
        ? "Sign up to save"
        : "Save score";
  dom.saveButton.disabled = state.lastSavedScore == null || state.savingScore;
}

function setAuthMessage(message) {
  dom.authMessage.textContent = message;
}

function setAuthLoading(loading) {
  dom.authSubmitButton.disabled = loading;
  dom.signupTab.disabled = loading;
  dom.loginTab.disabled = loading;
  if (loading) {
    dom.authSubmitButton.textContent = "Working...";
    return;
  }

  setAuthMode(state.authMode);
}

async function startCameraAndModels() {
  dom.playButton.disabled = true;
  setHomeMessage("Loading...");
  setCameraMessage("Loading models.", true);
  primeChallengeAudio();
  unlockMobileAudio();

  if (!window.isSecureContext) {
    setHomeMessage("Use localhost or HTTPS so the browser can open the camera.");
    dom.playButton.disabled = false;
    return;
  }

  try {
    await Promise.all([initModels(), initCamera()]);
    state.detectorsReady = true;
    dom.startButton.disabled = false;
    dom.startButton.textContent = "Reset";
    setCameraMessage("", false, true);
    resetRound();
    showGameScreen();
    if (!state.loopStarted) {
      state.loopStarted = true;
      requestAnimationFrame(detectLoop);
    }
  } catch (error) {
    console.error(error);
    setHomeMessage(readableError(error));
    setCameraMessage(readableError(error), false);
    dom.playButton.disabled = false;
  }
}

async function initModels() {
  if (state.handLandmarker && state.faceLandmarker) {
    return;
  }

  const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
  const baseOptions = { delegate: "GPU" };

  try {
    state.handLandmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: { ...baseOptions, modelAssetPath: HAND_MODEL },
      minHandDetectionConfidence: 0.58,
      minHandPresenceConfidence: 0.55,
      minTrackingConfidence: 0.55,
      numHands: 2,
      runningMode: "VIDEO",
    });

    state.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: { ...baseOptions, modelAssetPath: FACE_MODEL },
      minFaceDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      numFaces: 1,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
      runningMode: "VIDEO",
    });
  } catch (gpuError) {
    console.warn("GPU delegate failed, retrying on CPU.", gpuError);
    state.handLandmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: HAND_MODEL },
      minHandDetectionConfidence: 0.58,
      minHandPresenceConfidence: 0.55,
      minTrackingConfidence: 0.55,
      numHands: 2,
      runningMode: "VIDEO",
    });

    state.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: FACE_MODEL },
      minFaceDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      numFaces: 1,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
      runningMode: "VIDEO",
    });
  }
}

async function initCamera() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Camera access is not available in this browser.");
  }

  const stream = await getCameraStream();

  dom.video.srcObject = stream;
  await dom.video.play();
  state.cameraReady = true;
  resizeOverlay();
  window.addEventListener("resize", resizeOverlay);
}

async function getCameraStream() {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: getCameraConstraints(),
    });
  } catch (error) {
    if (!state.mobileExperience) {
      throw error;
    }

    console.warn("Mobile camera constraints failed, retrying with basic user-facing camera.", error);
    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: "user",
      },
    });
  }
}

function getCameraConstraints() {
  if (state.mobileExperience) {
    return {
      facingMode: "user",
      frameRate: { ideal: 30, max: 30 },
      height: { ideal: 360, max: 480 },
      width: { ideal: 640, max: 854 },
    };
  }

  return {
    facingMode: "user",
    frameRate: { ideal: 60, max: 60 },
    height: { ideal: 540 },
    width: { ideal: 960 },
  };
}

function detectLoop(now) {
  if (!state.cameraReady || !state.detectorsReady) {
    requestAnimationFrame(detectLoop);
    return;
  }

  if (dom.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    resizeOverlay();

    if (dom.video.currentTime !== state.lastVideoTime) {
      state.lastVideoTime = dom.video.currentTime;

      const hands = state.handLandmarker.detectForVideo(dom.video, now);

      const face = shouldRunFaceInference(now)
        ? state.faceLandmarker.detectForVideo(dom.video, now)
        : null;

      if (face) {
        state.lastFaceInferenceAt = now;
      }

      const analysis = analyzeFrame(hands, face, now);
      recordFrameTelemetry(analysis, now);
      updateGame(analysis, now);
    }
  }

  requestAnimationFrame(detectLoop);
}

function shouldRunFaceInference(now) {
  const idleInterval = state.mobileExperience ? MOBILE_FACE_INTERVAL_IDLE_MS : FACE_INTERVAL_IDLE_MS;
  const playingInterval = state.mobileExperience ? MOBILE_FACE_INTERVAL_PLAYING_MS : FACE_INTERVAL_PLAYING_MS;
  const interval = state.phase === "playing" ? playingInterval : idleInterval;
  return !state.lastFaceAnchor || now - state.lastFaceInferenceAt >= interval;
}

function analyzeFrame(handResult, faceResult, now) {
  const faceLandmarks = faceResult?.faceLandmarks?.[0] ?? null;
  const freshFace = faceLandmarks ? createFaceAnchor(faceLandmarks, now) : null;

  if (freshFace) {
    state.lastFaceAnchor = freshFace;
  }

  const faceAnchor =
    freshFace ??
    (state.lastFaceAnchor && now - state.lastFaceAnchor.seenAt < 8_000
      ? { ...state.lastFaceAnchor, stale: true }
      : null);

  const hands = (handResult.landmarks ?? []).map((landmarks, index) =>
    createHandAnchor(landmarks, handResult.handednesses?.[index]?.[0]?.categoryName)
  );

  const handAnalyses = hands.map((hand) => ({
    ...hand,
    coverScore: faceAnchor ? getCoverScore(hand, faceAnchor) : 0,
    activeScore: faceAnchor ? getActiveScore(hand, faceAnchor) : 0,
  }));

  const covering = handAnalyses
    .filter((hand) => hand.coverScore >= 0.45)
    .sort((a, b) => b.coverScore - a.coverScore)[0];

  const active = handAnalyses
    .filter((hand) => hand !== covering)
    .filter((hand) => hand.activeScore >= 0.58)
    .sort((a, b) => b.activeScore - a.activeScore)[0];

  const ready = Boolean(faceAnchor && covering && active);

  return {
    active,
    covering,
    faceAnchor,
    hands: handAnalyses,
    ready,
  };
}

function createFaceAnchor(landmarks, now) {
  const bounds = getBounds(landmarks);
  const nose = landmarks[1] ?? landmarks[4] ?? {
    x: bounds.center.x,
    y: bounds.center.y,
    z: 0,
  };

  return {
    bounds,
    height: bounds.height,
    landmarks,
    nose,
    seenAt: now,
    stale: false,
    width: bounds.width,
    zone: {
      cx: nose.x,
      cy: nose.y + bounds.height * 0.035,
      rx: clamp(bounds.width * 0.28, 0.055, 0.15),
      ry: clamp(bounds.height * 0.22, 0.055, 0.15),
    },
  };
}

function createHandAnchor(landmarks, handedness) {
  const bounds = getBounds(landmarks);
  const palm = averagePoints([landmarks[0], landmarks[5], landmarks[9], landmarks[13], landmarks[17]]);
  const thumbTip = landmarks[4];
  const indexTip = landmarks[8];
  const middleTip = landmarks[12];
  const pinkyTip = landmarks[20];
  const tips = [thumbTip, indexTip, middleTip, landmarks[16], pinkyTip];
  const handSize = Math.max(bounds.width, bounds.height);
  const thumbAbovePinky = pinkyTip.y - thumbTip.y;
  const verticalRatio = bounds.height / Math.max(bounds.width, 0.001);
  const verticalHand = thumbAbovePinky > 0.055 || verticalRatio > 1.2;
  const fingerTipCenter = averagePoints([indexTip, middleTip, landmarks[16], pinkyTip]);
  const fingerBaseCenter = averagePoints([landmarks[5], landmarks[9], landmarks[13], landmarks[17]]);
  const fingertipVector = {
    x: fingerTipCenter.x - fingerBaseCenter.x,
    y: fingerTipCenter.y - fingerBaseCenter.y,
  };

  return {
    bounds,
    fingerBaseCenter,
    fingerTipCenter,
    fingertipVector,
    handedness,
    handSize,
    landmarks,
    palm,
    tips,
    verticalHand,
  };
}

function getCoverScore(hand, face) {
  const zone = face.zone;
  const candidates = [hand.palm, ...hand.tips, hand.landmarks[6], hand.landmarks[10], hand.landmarks[14]];
  const insideCount = candidates.filter((point) => pointInEllipse(point, zone)).length;
  const palmNear = pointInEllipse(hand.palm, {
    ...zone,
    rx: zone.rx * 1.25,
    ry: zone.ry * 1.25,
  });
  const boundsOverlap = rectsOverlap(hand.bounds, {
    minX: zone.cx - zone.rx,
    maxX: zone.cx + zone.rx,
    minY: zone.cy - zone.ry,
    maxY: zone.cy + zone.ry,
  });

  return insideCount / candidates.length + (palmNear ? 0.35 : 0) + (boundsOverlap ? 0.2 : 0);
}

function getActiveScore(hand, face) {
  const largeEnough = hand.handSize >= clamp(face.width * 0.38, 0.09, 0.18);
  const fingerLength = Math.hypot(hand.fingertipVector.x, hand.fingertipVector.y);
  const fingersExtended = fingerLength >= clamp(face.width * 0.18, 0.03, 0.08);
  const awayFromFace = distance(hand.palm, face.nose) >= clamp(face.width * 0.42, 0.08, 0.18);
  const vertical = hand.verticalHand;

  return (largeEnough ? 0.32 : 0) + (fingersExtended ? 0.22 : 0) + (awayFromFace ? 0.16 : 0) + (vertical ? 0.4 : 0);
}

function updateGame(analysis, now) {
  if (state.phase === "playing") {
    updatePlaying(analysis, now);
    return;
  }

  if (state.phase === "countdown") {
    updateCountdown(analysis, now);
    return;
  }

  hideCountdown();

  if (state.phase === "finished") {
    setPrompt("Round complete");
    return;
  }

  if (analysis.ready) {
    if (state.autoStartArmed) {
      startCountdown(now);
      return;
    }

    setPrompt("Release, then lock in again");
    return;
  }

  state.autoStartArmed = true;
  setPrompt("Get in Scuba position");
}

function updateCountdown(analysis, now) {
  if (!analysis.ready) {
    state.phase = "idle";
    state.countdownEndAt = 0;
    state.autoStartArmed = true;
    state.flip = createFlipState();
    hideCountdown();
    stopChallengeAudio();
    setPrompt("Get in Scuba position");
    return;
  }

  const remaining = Math.max(0, state.countdownEndAt - now);
  showCountdown(Math.max(1, Math.ceil(remaining / 1000)));
  updateCountdownAudioVolume(remaining);
  setPrompt("", false);

  if (remaining <= 0) {
    startPlaying(now);
  }
}

function updatePlaying(analysis, now) {
  const remaining = Math.max(0, state.roundEndAt - now);
  updateTimer(remaining);

  if (remaining <= 0) {
    endRound();
    return;
  }

  if (!analysis.ready) {
    state.flip = createFlipState();
    setPrompt("", false);
    return;
  }

  updateFingertipFlip(analysis.active, now);
  setPrompt("", false);
}

function updateFingertipFlip(hand, now) {
  return updateFingertipFlipState(state.flip, hand, now, countPoint);
}

function countPoint(now, reason) {
  if (now - state.flip.lastCountAt >= SCORE_COOLDOWN_MS) {
    state.flip.lastCountAt = now;
    state.flip.lastCountReason = reason;
    recordCountTelemetry(now, reason);
    incrementScore();
    return true;
  }

  return false;
}

function incrementScore() {
  state.score += 1;
  dom.score.textContent = String(state.score);
  dom.scorePulse.classList.remove("show");
  void dom.scorePulse.offsetWidth;
  dom.scorePulse.classList.add("show");
}

function primeChallengeAudio() {
  if (!dom.challengeAudio) {
    return;
  }

  if (state.mobileExperience) {
    dom.challengeAudio.preload = "metadata";
  }

  dom.challengeAudio.volume = COUNTDOWN_AUDIO_START_VOLUME;
  dom.challengeAudio.load();
}

async function unlockMobileAudio() {
  if (!state.mobileExperience || !dom.challengeAudio || state.audioUnlocked) {
    return;
  }

  const previousTime = dom.challengeAudio.currentTime;
  const previousVolume = dom.challengeAudio.volume;

  try {
    dom.challengeAudio.volume = 0;
    await dom.challengeAudio.play();
    dom.challengeAudio.pause();
    dom.challengeAudio.currentTime = previousTime || 0;
    state.audioUnlocked = true;
  } catch (error) {
    console.warn("Mobile audio unlock was blocked.", error);
  } finally {
    dom.challengeAudio.volume = previousVolume || COUNTDOWN_AUDIO_START_VOLUME;
  }
}

async function startCountdownAudio() {
  if (!dom.challengeAudio || state.phase !== "countdown") {
    return;
  }

  const sessionId = ++state.audioSessionId;
  dom.challengeAudio.currentTime = 0;
  dom.challengeAudio.volume = COUNTDOWN_AUDIO_START_VOLUME;

  try {
    await dom.challengeAudio.play();
  } catch (error) {
    if (sessionId !== state.audioSessionId || !isAudioActivePhase()) {
      return;
    }

    console.warn("Challenge audio could not autoplay.", error);
    return;
  }

  if (sessionId !== state.audioSessionId || !isAudioActivePhase()) {
    dom.challengeAudio.pause();
    dom.challengeAudio.currentTime = 0;
    dom.challengeAudio.volume = COUNTDOWN_AUDIO_START_VOLUME;
  }
}

function updateCountdownAudioVolume(remainingMs) {
  if (!dom.challengeAudio || state.phase !== "countdown") {
    return;
  }

  const progress = clamp(1 - remainingMs / COUNTDOWN_MS, 0, 1);
  const volume =
    COUNTDOWN_AUDIO_START_VOLUME +
    (CHALLENGE_AUDIO_FULL_VOLUME - COUNTDOWN_AUDIO_START_VOLUME) * progress;
  dom.challengeAudio.volume = volume;
}

function finishAudioRamp() {
  if (!dom.challengeAudio || state.phase !== "playing") {
    return;
  }

  dom.challengeAudio.volume = CHALLENGE_AUDIO_FULL_VOLUME;
}

function stopChallengeAudio() {
  if (!dom.challengeAudio) {
    return;
  }

  state.audioSessionId += 1;
  dom.challengeAudio.pause();
  dom.challengeAudio.currentTime = 0;
  dom.challengeAudio.volume = COUNTDOWN_AUDIO_START_VOLUME;
}

function isAudioActivePhase() {
  return state.phase === "countdown" || state.phase === "playing";
}

function resetRound({ requirePoseRelease = false } = {}) {
  if (!state.detectorsReady || !state.cameraReady) {
    return;
  }

  state.phase = "idle";
  state.autoStartArmed = !requirePoseRelease;
  state.countdownEndAt = 0;
  state.lastSavedScore = null;
  state.pendingSaveRequested = false;
  state.flip = createFlipState();
  state.roundEndAt = 0;
  state.score = 0;
  state.savingScore = false;
  state.telemetry = createTelemetryState();
  state.remoteRunStartPromise = null;
  dom.score.textContent = "0";
  dom.startButton.textContent = "Reset";
  setPrompt("Get in Scuba position");
  updateTimer(ROUND_LENGTH_MS);
  hideCountdown();
  stopChallengeAudio();
  renderSaveState();
}

function startCountdown(now) {
  state.phase = "countdown";
  state.autoStartArmed = false;
  state.countdownEndAt = now + COUNTDOWN_MS;
  state.lastSavedScore = null;
  state.pendingSaveRequested = false;
  state.flip = createFlipState();
  state.score = 0;
  state.savingScore = false;
  state.telemetry = createTelemetryState();
  state.remoteRunStartPromise = null;
  dom.score.textContent = "0";
  dom.startButton.textContent = "Reset";
  updateTimer(ROUND_LENGTH_MS);
  setPrompt("", false);
  showCountdown(3);
  startCountdownAudio();
  void beginRunSession();
  renderSaveState();
}

function startPlaying(now) {
  state.phase = "playing";
  state.flip = createFlipState();
  state.roundEndAt = now + ROUND_LENGTH_MS;
  state.telemetry.challengeStartedAt = new Date().toISOString();
  state.telemetry.challengeStartedNow = now;
  setPrompt("", false);
  hideCountdown();
  finishAudioRamp();
}

function endRound() {
  state.phase = "finished";
  state.lastSavedScore = state.score;
  state.pendingSaveRequested = false;
  state.telemetry.clientFinishedAt = new Date().toISOString();
  state.telemetry.durationMs = ROUND_LENGTH_MS;
  void completeRunSession(state.telemetry, state.remoteRunStartPromise);
  setPrompt("Round complete");
  dom.startButton.textContent = "New round";
  hideCountdown();
  updateTimer(0);
  stopChallengeAudio();
  renderSaveState();
}

async function saveScore(event) {
  event.preventDefault();
  await saveCurrentScore();
}

async function saveCurrentScore() {
  if (state.lastSavedScore == null) {
    return false;
  }

  if (remoteLeaderboardEnabled && (!state.authSession || !state.profile)) {
    state.pendingSaveRequested = true;
    renderSaveState("Sign up or log in to put this score on the leaderboard.");
    if (state.authSession && !state.profile) {
      await clearIncompleteAccountSession();
      renderAuthState();
    }
    openAuthModal("signup");
    return false;
  }

  const entry = {
    createdAt: new Date().toISOString(),
    clientVersion: APP_VERSION,
    sessionId: state.telemetry.sessionId,
    name: state.profile?.username ?? "Guest",
    score: state.lastSavedScore,
    telemetry: finalizeTelemetry(),
  };

  state.savingScore = true;
  renderSaveState("Saving score...");

  try {
    if (remoteLeaderboardEnabled) {
      await ensureRunSessionStarted();
    }

    await submitScore(entry);
  } catch (error) {
    console.error(error);
    state.savingScore = false;
    renderSaveState(error.message || "Score could not be saved.");
    return false;
  }

  state.savingScore = false;
  state.lastSavedScore = null;
  state.pendingSaveRequested = false;
  renderSaveState("Score saved.");

  try {
    await renderLeaderboard();
  } catch (error) {
    console.warn("Leaderboard refresh failed after score save.", error);
    setLeaderboardStatus("Score saved. Leaderboard refresh failed.");
  }

  return true;
}

async function submitScore(entry) {
  if (remoteLeaderboardEnabled) {
    await finishRemoteRun({
      score: entry.score,
      sessionId: entry.sessionId,
      telemetry: entry.telemetry,
    }, getAccessToken());
    return;
  }

  const existing = getLocalLeaderboard();
  existing.push(entry);
  existing.sort((a, b) => b.score - a.score || new Date(a.createdAt) - new Date(b.createdAt));
  localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(existing.slice(0, 10)));
}

async function completeRunSession(telemetry, startPromise) {
  if (!remoteLeaderboardEnabled) {
    return;
  }

  try {
    if (!telemetry.remoteStartOk && startPromise) {
      await startPromise;
    }

    if (!telemetry.remoteStartOk) {
      await startRemoteRun({
        clientStartedAt: telemetry.clientStartedAt,
        clientVersion: APP_VERSION,
        sessionId: telemetry.sessionId,
      });
      telemetry.remoteStartOk = true;
    }

    await completeRemoteRun({
      clientCompletedAt: telemetry.clientFinishedAt,
      sessionId: telemetry.sessionId,
    });
  } catch (error) {
    console.warn("Remote round completion tracking failed.", error);
  }
}

async function savePendingScoreIfReady() {
  if (!state.pendingSaveRequested || state.lastSavedScore == null) {
    return false;
  }

  if (remoteLeaderboardEnabled && (!state.authSession || !state.profile)) {
    return false;
  }

  return saveCurrentScore();
}

async function renderLeaderboard(target = {}) {
  const list = target.list ?? dom.leaderboard;
  const status = target.status ?? dom.leaderboardStatus;
  const { leaders, source } = await getLeaderboard();
  list.innerHTML = "";
  setLeaderboardStatus(getLeaderboardStatusLabel(source), status);

  if (leaders.length === 0) {
    const empty = document.createElement("li");
    empty.innerHTML = `<span class="rank">--</span><span class="leader-name">No runs yet<span class="leader-meta">${remoteLeaderboardEnabled ? "Global board" : "Local dev board"}</span></span><span class="leader-score">0</span>`;
    list.append(empty);
    return;
  }

  leaders.forEach((entry, index) => {
    const item = document.createElement("li");
    const dateLabel = formatLeaderboardDate(entry.createdAt);
    const meta = dateLabel ? `<span class="leader-meta">${escapeHtml(dateLabel)}</span>` : "";
    const rank = entry.rank ?? index + 1;
    const trophy = getLeaderboardTrophy(rank);
    const trophyMarkup = trophy
      ? `<span class="${trophy.className}" role="img" aria-label="${escapeHtml(trophy.label)}">${trophy.symbol}</span>`
      : "";
    item.innerHTML = `
      <span class="rank">${index + 1}</span>
      <span class="leader-name"><span class="leader-row-name"><span class="leader-username">${escapeHtml(entry.name || "Player")}</span>${trophyMarkup}</span>${meta}</span>
      <span class="leader-score">${Number.isFinite(Number(entry.score)) ? Number(entry.score) : 0}</span>
    `;
    list.append(item);
  });
}

function resizeOverlay() {
  const videoWidth = dom.video.videoWidth || 1280;
  const videoHeight = dom.video.videoHeight || 720;
  if (dom.overlay.width !== videoWidth || dom.overlay.height !== videoHeight) {
    dom.overlay.width = videoWidth;
    dom.overlay.height = videoHeight;
    dom.cameraFrame.style.aspectRatio = `${videoWidth} / ${videoHeight}`;
  }
}

function getBounds(points) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return {
    center: {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    },
    height: maxY - minY,
    maxX,
    maxY,
    minX,
    minY,
    width: maxX - minX,
  };
}

function averagePoints(points) {
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
    z: points.reduce((sum, point) => sum + (point.z ?? 0), 0) / points.length,
  };
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function pointInEllipse(point, zone) {
  const dx = (point.x - zone.cx) / zone.rx;
  const dy = (point.y - zone.cy) / zone.ry;
  return dx * dx + dy * dy <= 1;
}

function rectsOverlap(a, b) {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function updateTimer(ms) {
  dom.timer.textContent = (ms / 1000).toFixed(1);
}

function showCountdown(value) {
  dom.countdown.textContent = String(value);
  dom.countdown.classList.remove("hidden");
}

function hideCountdown() {
  dom.countdown.classList.add("hidden");
}

function setPrompt(message, visible = true) {
  dom.feedback.textContent = message;
  dom.feedback.classList.toggle("hidden", !visible);
}

function setCameraMessage(message, loading, hidden = false) {
  dom.cameraMessage.textContent = message;
  dom.cameraEmpty.classList.toggle("loading", Boolean(loading));
  dom.cameraEmpty.classList.toggle("hidden", Boolean(hidden));
}

function setHomeMessage(message) {
  dom.homeMessage.textContent = message;
}

function showGameScreen() {
  dom.homeScreen.classList.add("hidden");
  dom.gameScreen.classList.remove("hidden");
}

async function getLeaderboard() {
  if (remoteLeaderboardEnabled) {
    try {
      const response = await fetchRemoteLeaderboard();
      return {
        leaders: response.leaders ?? [],
        source: "global",
      };
    } catch (error) {
      console.warn("Remote leaderboard unavailable, falling back to local scores.", error);
      return {
        leaders: getLocalLeaderboard(),
        source: "fallback",
      };
    }
  }

  return {
    leaders: getLocalLeaderboard(),
    source: "local",
  };
}

function getLeaderboardStatusLabel(source) {
  if (source === "fallback") {
    return "Global leaderboard unavailable. Showing local scores.";
  }

  return source === "global" ? "Global leaderboard" : "Local leaderboard";
}

function getLocalLeaderboard() {
  try {
    return JSON.parse(localStorage.getItem(LEADERBOARD_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function createTelemetryState() {
  return {
    averageFps: 0,
    challengeStartedAt: null,
    challengeStartedNow: 0,
    clientFinishedAt: null,
    clientStartedAt: new Date().toISOString(),
    countEvents: [],
    durationMs: 0,
    frameCount: 0,
    lastFrameNow: 0,
    readyFrameCount: 0,
    remoteStartOk: false,
    sessionId: crypto.randomUUID(),
  };
}

async function beginRunSession() {
  if (!remoteLeaderboardEnabled) {
    return;
  }

  try {
    await ensureRunSessionStarted();
  } catch (error) {
    console.warn("Remote run start failed.", error);
    setLeaderboardStatus("Global save may be unavailable.");
  }
}

async function ensureRunSessionStarted() {
  if (!remoteLeaderboardEnabled) {
    return;
  }

  if (state.telemetry.remoteStartOk) {
    return;
  }

  if (!state.remoteRunStartPromise) {
    const telemetry = state.telemetry;
    state.remoteRunStartPromise = startRemoteRun({
      clientStartedAt: telemetry.clientStartedAt,
      clientVersion: APP_VERSION,
      sessionId: telemetry.sessionId,
    })
      .then(() => {
        if (state.telemetry === telemetry) {
          telemetry.remoteStartOk = true;
        }
      })
      .catch((error) => {
        state.remoteRunStartPromise = null;
        throw error;
      });
  }

  await state.remoteRunStartPromise;
}

function recordFrameTelemetry(analysis, now) {
  if (state.phase !== "playing" || !state.telemetry.challengeStartedAt) {
    return;
  }

  state.telemetry.frameCount += 1;
  if (analysis.ready) {
    state.telemetry.readyFrameCount += 1;
  }

  state.telemetry.lastFrameNow = now;
}

function recordCountTelemetry(now, reason) {
  if (state.phase !== "playing" || !state.telemetry.challengeStartedAt) {
    return;
  }

  state.telemetry.countEvents.push({
    reason,
    t: Math.max(0, Math.round(now - state.telemetry.challengeStartedNow)),
  });
}

function finalizeTelemetry() {
  const durationMs = state.telemetry.durationMs || ROUND_LENGTH_MS;
  const frameCount = state.telemetry.frameCount;
  const readyFrameCount = state.telemetry.readyFrameCount;

  return {
    averageFps: Number((frameCount / Math.max(durationMs / 1000, 0.001)).toFixed(2)),
    challengeStartedAt: state.telemetry.challengeStartedAt,
    clientFinishedAt: state.telemetry.clientFinishedAt ?? new Date().toISOString(),
    clientStartedAt: state.telemetry.clientStartedAt,
    clientVersion: APP_VERSION,
    countEvents: state.telemetry.countEvents,
    durationMs,
    frameCount,
    readyFrameCount,
    readyFrameRatio: Number((readyFrameCount / Math.max(frameCount, 1)).toFixed(3)),
    remoteStartOk: state.telemetry.remoteStartOk,
  };
}

function setLeaderboardStatus(message, target = dom.leaderboardStatus) {
  if (target) {
    target.textContent = message;
  }
}

function isMobileExperience() {
  const userAgent = navigator.userAgent ?? "";
  const mobileUserAgent = /Android|iPhone|iPad|iPod/i.test(userAgent);
  const iPadDesktopMode = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;

  return mobileUserAgent || iPadDesktopMode || coarsePointer;
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return entities[char];
  });
}

function readableError(error) {
  if (error?.name === "NotAllowedError") {
    return "Camera permission was blocked.";
  }

  if (error?.name === "NotFoundError") {
    return "No camera was found.";
  }

  return "Could not start the camera or model.";
}
